import { getQuotePayYears } from '../guidedPlanningPreferences';
import {
  LINCOLN_MONEYGUARD_2025_BANDS,
  LINCOLN_MONEYGUARD_2025_MAX_FACE_AMOUNT,
  LINCOLN_MONEYGUARD_2025_TEN_PAY_PRICING,
} from './lincolnPricingConfig';

type LincolnQuote = Pick<
  PolicyQuote,
  'premiumAmount' | 'deathBenefitInitial' | 'cobPaymentOption' | 'productFundingOption'
>;

const PREMIUM_CEILING_STEP = 100;

/**
 * Room left under the issue limit for the estimate's own error. The estimate usually lands
 * within a few dollars of the carrier, but a single pay quote estimated at $499,978 came back
 * from the engine as $500,001 and was refused. 0.05% ($250) is ten times that miss.
 */
const FACE_AMOUNT_ESTIMATE_TOLERANCE = 0.0005;

/**
 * Returns the pricing multiplier for a premium, or undefined when the premium is above the
 * published band table.
 *
 * The two edges are handled deliberately differently. Above the last band, flat-clamping the
 * edge multiplier is what let benefits scale linearly to any premium while ROI stayed
 * constant (the A2 defect), so the premium is refused. Below the first band the flat clamp is
 * kept: extrapolation there shrinks benefits rather than inflating them, the premium is
 * already floored by investmentMin, and refusing would strand real quotes — Lincoln's table
 * starts at $50k while some stored single pay premiums start at $10k.
 */
export function getMultiplierForPremium(premium: number): number | undefined {
  const bands = LINCOLN_MONEYGUARD_2025_BANDS;
  if (bands.length === 0) return undefined;

  const first = bands[0];
  const last = bands[bands.length - 1];

  if (premium > last.premium) return undefined;
  if (premium <= first.premium) return first.multiplier;
  if (premium === last.premium) return last.multiplier;

  for (let i = 0; i < bands.length - 1; i++) {
    if (premium >= bands[i].premium && premium < bands[i + 1].premium) {
      const t = (premium - bands[i].premium) / (bands[i + 1].premium - bands[i].premium);
      return bands[i].multiplier + t * (bands[i + 1].multiplier - bands[i].multiplier);
    }
  }

  return undefined;
}

/**
 * The face amount a 10-Pay quote buys at another annual premium, or undefined when the rule
 * cannot price it. See LINCOLN_MONEYGUARD_2025_TEN_PAY_PRICING for the rule and how it was
 * measured.
 */
export function getLincolnTenPayFaceAmount(
  originalPremium: number,
  originalFaceAmount: number,
  premium: number,
): number | undefined {
  const { annualCharge, smallFaceThreshold, smallFaceSurchargeRate, maxPremium } =
    LINCOLN_MONEYGUARD_2025_TEN_PAY_PRICING;

  if (premium > maxPremium || !(originalFaceAmount > 0)) return undefined;
  if (premium === originalPremium) return originalFaceAmount;

  // What a quote sitting exactly on the threshold would pay in surcharge on its whole face.
  const fullSurcharge = smallFaceSurchargeRate * smallFaceThreshold;

  // The quote's own cost per dollar of face amount, read off its stored premium.
  const rate =
    originalFaceAmount >= smallFaceThreshold
      ? (originalPremium - annualCharge) / originalFaceAmount
      : (originalPremium - annualCharge - fullSurcharge) / originalFaceAmount +
        smallFaceSurchargeRate;
  if (!(rate > smallFaceSurchargeRate)) return undefined;

  const faceAmount = (premium - annualCharge) / rate;
  if (faceAmount >= smallFaceThreshold) return faceAmount;

  const smallFaceAmount =
    (premium - annualCharge - fullSurcharge) / (rate - smallFaceSurchargeRate);
  return smallFaceAmount > 0 ? smallFaceAmount : undefined;
}

/**
 * The factor a quote's benefits scale by at another premium, or undefined when no validated
 * curve prices it.
 *
 * Single pay and 10-Pay are corrected separately because the single pay table spans
 * $50k–$500k: every 10-Pay premium ($8k–$30k) sits below its first row, flat-clamps to one
 * multiplier, and would be left priced linearly. A null pay-years (single premium, or a row
 * carrying no payment fields) uses the single pay table. Any other fixed-year term was never
 * calibrated, and callers must refuse to reprice it rather than borrow a curve the carrier
 * never quoted.
 */
export function getLincolnScalingFactor(
  quote: LincolnQuote,
  newPremium: number,
): number | undefined {
  const payYears = getQuotePayYears(quote);

  if (payYears === 10) {
    const faceAmount = getLincolnTenPayFaceAmount(
      quote.premiumAmount,
      quote.deathBenefitInitial,
      newPremium,
    );
    return faceAmount === undefined ? undefined : faceAmount / quote.deathBenefitInitial;
  }
  if (payYears !== null) return undefined;

  const originalMultiplier = getMultiplierForPremium(quote.premiumAmount);
  const newMultiplier = getMultiplierForPremium(newPremium);
  // originalMultiplier is a divisor, so 0 is rejected alongside undefined.
  if (newMultiplier === undefined || !originalMultiplier) return undefined;
  return (newPremium / quote.premiumAmount) * (newMultiplier / originalMultiplier);
}

/** True when an estimated face amount is too close to Lincoln's issue limit to be safe. */
export function exceedsLincolnFaceAmountLimit(estimatedFaceAmount: number): boolean {
  return (
    estimatedFaceAmount >
    LINCOLN_MONEYGUARD_2025_MAX_FACE_AMOUNT * (1 - FACE_AMOUNT_ESTIMATE_TOLERANCE)
  );
}

/**
 * The highest premium above the quote's own whose face amount stays inside Lincoln's issue
 * limit, in $100 steps so the card shows a round premium. Falls back to the quote's own
 * premium, which the carrier did quote, when no higher step fits.
 */
export function getLincolnMaximumPremium(quote: LincolnQuote): number {
  const isIssuable = (step: number) => {
    const factor = getLincolnScalingFactor(quote, step * PREMIUM_CEILING_STEP);
    return (
      factor !== undefined && !exceedsLincolnFaceAmountLimit(quote.deathBenefitInitial * factor)
    );
  };

  const highestPricedPremium =
    getQuotePayYears(quote) === 10
      ? LINCOLN_MONEYGUARD_2025_TEN_PAY_PRICING.maxPremium
      : LINCOLN_MONEYGUARD_2025_BANDS[LINCOLN_MONEYGUARD_2025_BANDS.length - 1].premium;

  // The face amount only rises with the premium, so the last issuable step can be bisected.
  let low = Math.floor(quote.premiumAmount / PREMIUM_CEILING_STEP);
  let high = Math.floor(highestPricedPremium / PREMIUM_CEILING_STEP);
  if (high <= low || !isIssuable(low + 1)) return quote.premiumAmount;
  low += 1;

  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (isIssuable(middle)) low = middle;
    else high = middle - 1;
  }

  return low * PREMIUM_CEILING_STEP;
}
